import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft,
  Plus,
  Copy,
  FileText,
  QrCode,
  RefreshCw,
} from 'lucide-react';
import { Button } from '@/app/components/ui/button';
import { Badge } from '@/app/components/ui/badge';
import { Card, CardContent } from '@/app/components/ui/card';
import { Skeleton } from '@/app/components/ui/skeleton';
import { useProject, useProjectQRCode } from '@/hooks/use-projects';
import { useJSAs, useDuplicateJSA } from '@/hooks/use-safety';
import { QRCodeModal } from '@/components/safety/qr-code-modal';
import { ModuleDisabledState } from '@/app/components/module-disabled-state';
import { usePlatformModules } from '@/hooks/use-modules';
import type { JSAStatus } from '@/lib/types';
import { formatDateOnly } from '@/lib/date-utils';
import { PreviousDayJSAPrompt } from '@/app/components/safety/previous-day-jsa-prompt';

const statusColors: Record<JSAStatus, string> = {
  draft: 'bg-gray-100 text-gray-800',
  pending_signatures: 'bg-yellow-100 text-yellow-800',
  active: 'bg-green-100 text-green-800',
  closed: 'bg-blue-100 text-blue-800',
  archived: 'bg-gray-100 text-gray-600',
};

const statusLabels: Record<JSAStatus, string> = {
  draft: 'Draft',
  pending_signatures: 'Pending',
  active: 'Active',
  closed: 'Closed',
  archived: 'Archived',
};

export function JobJSAsPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const projectId = parseInt(id ?? '0');

  const [showQR, setShowQR] = useState(false);

  const { data: project, isLoading: projectLoading } = useProject(projectId);
  const { data: qrData, isLoading: qrLoading } = useProjectQRCode(projectId, showQR);
  const { isModuleEnabled, isLoading: modulesLoading } = usePlatformModules();
  const safetyEnabled = isModuleEnabled('safety');
  const { data: jsaData, isLoading: jsaLoading, refetch } = useJSAs({
    project: projectId,
    page_size: 50,
    ordering: '-date',
  }, safetyEnabled);
  const duplicateMutation = useDuplicateJSA();

  const jsas = jsaData?.results || [];
  const mostRecentJSA = jsas.length > 0 ? jsas[0] : null;

  const handleDuplicate = (jsaId: number) => {
    duplicateMutation.mutate(jsaId, {
      onSuccess: (newJSA) => {
        navigate(`/safety/jsas/${newJSA.id}`);
      },
    });
  };

  if (projectLoading || modulesLoading) {
    return (
      <div className="p-4 sm:p-6 space-y-4">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  if (!safetyEnabled) {
    return (
      <div className="p-4 sm:p-6 max-w-2xl mx-auto">
        <ModuleDisabledState moduleKey="safety" />
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 space-y-4 max-w-2xl mx-auto">
      <PreviousDayJSAPrompt projectId={projectId} enabled={safetyEnabled} />
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate(`/projects/${projectId}`)}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1 min-w-0">
          <h1 className="text-xl font-bold text-gray-900 truncate">
            {project?.name || 'Project'} &mdash; JSAs
          </h1>
          {project && (
            <p className="text-sm text-gray-500 truncate">
              {project.project_number}
              {project.location ? ` \u00B7 ${project.location}` : ''}
            </p>
          )}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="flex flex-wrap gap-2">
        {mostRecentJSA && (
          <Button
            onClick={() => handleDuplicate(mostRecentJSA.id)}
            disabled={duplicateMutation.isPending}
            className="flex-1 sm:flex-none"
          >
            <Copy className="h-4 w-4 mr-2" />
            {duplicateMutation.isPending ? 'Duplicating...' : 'Duplicate Latest JSA'}
          </Button>
        )}
        <Button variant="outline" asChild className="flex-1 sm:flex-none">
          <Link to={`/safety/jsas/new?project=${projectId}`}>
            <Plus className="h-4 w-4 mr-2" />
            New JSA
          </Link>
        </Button>
        <Button variant="outline" size="icon" onClick={() => setShowQR(true)}>
          <QrCode className="h-4 w-4" />
        </Button>
        <Button variant="outline" size="icon" onClick={() => refetch()}>
          <RefreshCw className="h-4 w-4" />
        </Button>
      </div>

      {/* JSA List */}
      {jsaLoading ? (
        <div className="space-y-3">
          {[...Array(3)].map((_, i) => (
            <Skeleton key={i} className="h-20 w-full" />
          ))}
        </div>
      ) : jsas.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center">
            <FileText className="h-12 w-12 mx-auto text-gray-400 mb-3" />
            <p className="text-gray-600 mb-4">No JSAs yet for this project.</p>
            <Button asChild>
              <Link to={`/safety/jsas/new?project=${projectId}`}>
                <Plus className="h-4 w-4 mr-2" />
                Create First JSA
              </Link>
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {jsas.map((jsa, index) => (
            <Card
              key={jsa.id}
              className={index === 0 ? 'border-blue-200 bg-blue-50/30' : ''}
            >
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <Link
                      to={`/safety/jsas/${jsa.id}`}
                      className="font-medium text-blue-600 hover:underline block truncate"
                    >
                      {jsa.title}
                    </Link>
                    <div className="flex flex-wrap items-center gap-2 mt-1 text-sm text-gray-500">
                      <span>{formatDateOnly(jsa.date)}</span>
                      {jsa.location && (
                        <>
                          <span>&middot;</span>
                          <span className="truncate">{jsa.location}</span>
                        </>
                      )}
                      {jsa.signing_progress && jsa.signing_progress !== '0/0' && (
                        <>
                          <span>&middot;</span>
                          <span>{jsa.signing_progress} signed</span>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <Badge className={statusColors[jsa.status]}>
                      {statusLabels[jsa.status]}
                    </Badge>
                    {index === 0 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDuplicate(jsa.id)}
                        disabled={duplicateMutation.isPending}
                        title="Duplicate this JSA"
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* QR Code Modal */}
      <QRCodeModal
        open={showQR}
        onOpenChange={setShowQR}
        qrDataUrl={qrData?.qr_data_url}
        title={project?.name || 'Project'}
        subtitle={project?.location}
        isLoading={qrLoading}
      />
    </div>
  );
}
